import { PhasrApplyActionPanelView } from '../view/PhasrApplyActionPanelView';
import { templatePhasrApplyActionPanel } from '../view/template';
import { PhasrApplyActionPanelRepository } from '../repository/PhasrApplyActionPanelRepository';
import { BaseWebComponentController, IListenerConfig } from '../../framework/BaseWebComponentController';
import { PhasrApplyActionPanelWebComponent } from '../index';
import { Collections, PhasrApplyActionPanelEvents, PhasrApplyActionSchema } from '../types';
import { translateFn } from '../../utils/translator.helper';
import { GzDialogWebComponent } from '../../ui/gzDialog';
import { BehavioralProfileTypes } from '../../phasrRecommendations/phasrBehavioralProfilesComponent/types';
import { ALLOW_ACTION, RESTRICT_ACTION } from "../../phasrRecommendations/phasrRecommendationsGrid/types";
import { PhasrEvents } from '../../types/common/phasrTypes';
import { ColumnKeys as ColumnKeysMonitoredRules } from '../../phasrMonitoredRules/phasrMonitoredRulesGrid/types';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { NavigationHelper } from '@nirvana/shared/navigation';

export class PhasrApplyActionPanelController extends BaseWebComponentController {
  public component: PhasrApplyActionPanelWebComponent;
  public dataRepository: PhasrApplyActionPanelRepository;
  public view: PhasrApplyActionPanelView;
  public viewConstructor: typeof PhasrApplyActionPanelView;
  public dataRepositoryConstructor: typeof PhasrApplyActionPanelRepository;

  private behavioralProfilesType: BehavioralProfileTypes = BehavioralProfileTypes.DIRECT_CONTROL;

  constructor(component: PhasrApplyActionPanelWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: PhasrApplyActionPanelView,
      viewTemplate: templatePhasrApplyActionPanel,
      dataRepositoryConstructor: PhasrApplyActionPanelRepository,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$applyButton,
        event: 'click',
        callback: this.applyActionConfirmationWindow,
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.emitCloseWindowEvent,
      },
      {
        element: this.view.dom.$behavioralProfiles,
        event: PhasrApplyActionPanelEvents.BEHAVIORAL_PROFILE_TYPE_CHANGED,
        callback: this.onBehavioralProfileTypeChanged,
      },
      {
        element: this.view.dom.$behavioralProfiles,
        event: PhasrEvents.SELECTED_PROFILES_CHANGED,
        callback: this.onSelectedProfilesChanged,
      },
      {
        element: this.view.dom.$monitoredRule,
        event: 'click',
        callback: this.handleRuleNameClick,
      }
    ];
  }

  public async getDataForView(): Promise<PhasrApplyActionSchema> {
    return {
      collection: this.component.collection,
      ...this.component.getGridData()
    };
  }

  private onSelectedProfilesChanged(e: CustomEvent): void {
    const selectedProfiles = e.detail.count;
    switch (this.component.collection) {
      case Collections.RECOMMENDATIONS:
        this.view.dom.$applyButton.disabled = selectedProfiles === 0;
        break;
      case Collections.RULES:
        this.view.dom.$applyButton.disabled = this.behavioralProfilesType === BehavioralProfileTypes.AUTOPILOT;
        break;
      default:
        this.view.dom.$applyButton.disabled = false;
    }
  }

  private emitCloseWindowEvent(): void {
    this.component.dispatchEvent(new CustomEvent(PhasrApplyActionPanelEvents.CANCEL));
  }

  private onBehavioralProfileTypeChanged(e: CustomEvent): void {
    try {
      if (e.detail.selectedValue === BehavioralProfileTypes.AUTOPILOT) {
        analytics.trackPageInteraction({
          reference: 'phasr-monitored-rules-edit-access',
          asset: 'autopilot-view',
        });
      }
    } finally {
      this.behavioralProfilesType = e.detail.selectedValue;
      this.view.dom.$applyButton.disabled = e.detail.selectedValue === BehavioralProfileTypes.AUTOPILOT;
    }
  }

  public applyActionConfirmationWindow(): void {
    const {windowBodyHtml, windowFooterHtml, windowTitle} = this.getDialogHtmlComponents();

    const dialog = GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        this.emitApplyActionEvent();
        dialog.close();
      },
      null,
      false
    );
  }

  private getDialogHtmlComponents(): { windowBodyHtml: string, windowFooterHtml: string, windowTitle: string } {
    let windowBodyHtml: string;
    let windowFooterHtml: string;
    let windowTitle: string;

    switch (this.component.collection) {
      case Collections.RECOMMENDATIONS:
        const {bodyText, footerText, title} = this.getRecommendationsTexts();

        windowBodyHtml = `<p>${bodyText}</p>`;
        windowFooterHtml = `
          <gz-button id='confirm' type='full' color='primary' class='mr-2'>
            ${footerText}
          </gz-button>
          <gz-button id='cancel' type='outline' color='secondary'>
            ${translateFn('Cancel')}
          </gz-button>
        `;
        windowTitle = title;
        break;
      case Collections.RULES:
        windowBodyHtml = `<p>${translateFn('Access settings will be changed for multiple behavioral profiles.')}</p>`;
        windowFooterHtml = `
          <gz-button id='confirm' type='full' color='primary' class='mr-2'>
            ${translateFn('Edit access')}
          </gz-button>
          <gz-button id='cancel' type='outline' color='secondary'>
            ${translateFn('Cancel')}
          </gz-button>
        `;
        windowTitle = translateFn('Edit access?');
        break;
      default:
        windowBodyHtml = `<p>${translateFn('N/A.')}}</p>`;
        windowFooterHtml = `
          <gz-button id='confirm' type='full' color='primary' class='mr-2'>
            ${translateFn('N/A')}
          </gz-button>
          <gz-button id='cancel' type='outline' color='secondary'>
            ${translateFn('Cancel')}
          </gz-button>
        `;
        windowTitle = translateFn('N/A');
    }

    return {windowBodyHtml, windowFooterHtml, windowTitle};
  }

  private getRecommendationsTexts(): { bodyText: string, footerText: string, title: string } {
    let bodyText = 'N/A';
    let footerText = 'N/A';
    let title = 'N/A';

    if (this.component.getGridData().action === RESTRICT_ACTION) {
      bodyText = translateFn('Access will be restricted for the selected behavioral profiles.');
      footerText = translateFn('Restrict access');
      title = translateFn('Restrict access?');
    }
    if (this.component.getGridData().action === ALLOW_ACTION) {
      bodyText = translateFn('Access will be allowed for the selected behavioral profiles.');
      footerText = translateFn('Allow access');
      title = translateFn('Allow access?');
    }

    return {bodyText, footerText, title};
  }

  private emitApplyActionEvent(): void {
    this.component.dispatchEvent(
      new CustomEvent(PhasrApplyActionPanelEvents.APPLY_ACTION,{
        detail: {
          changes: this.view.dom.$behavioralProfiles.getSelectedOutput()
        }
      })
    );
  }

  private handleRuleNameClick(e: CustomEvent): void {
    const data = this.component.getGridData();
    const params = {
      filters: [
        {
          [ColumnKeysMonitoredRules.COMPANY]: {
            exactMatch: data.companyId,
          },
        },
        {
          [ColumnKeysMonitoredRules.RULE_TRIGGER_DATE]: {
            timeRange: {
              kind: TimeRangeKind.ALL_TO_DATE,
            },
          }
        },
        {
          [ColumnKeysMonitoredRules.RULE_NAME]: {
            complexEnumFilter: [
              {
                value: data.ruleId,
                label: data.rule,
              }
            ]
          }
        }
      ],
      openSideMenuForFirstItem: true,
    };

    NavigationHelper.navigateTo('/phasr-monitored-rules', params, true);
  }
}
